import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isPasswordComplexEnough } from "@autorepair/contracts";
import { authApi, tenantSettingsApi, ApiError } from "../lib/api";
import { useAuth } from "../lib/useAuth";
import { useConfirmGate } from "../lib/useConfirm";
import { useNotice } from "../lib/useNotice";
import { TwoFactorAuthCard } from "../components/TwoFactorAuthCard";
import {
  Eyebrow,
  ModernButton,
  ModernCard,
  ModernConfirmDialog,
  ModernField,
  ModernInput,
  ModernNoticeBanner,
  ModernPasswordInput,
  ModernPasswordRequirementsList,
  ModernPasswordStrengthMeter,
} from "../components/ui-modern";

/**
 * The modern theme's User Profile screen — see docs/FRONTEND_BACKLOG.md
 * Round 62 (owner profile / admin panel, the last 2 screens outside the
 * original 11-mockup rollout). No mockup screen exists for this page — it
 * follows SettingsPageModern.tsx's own card-per-section layout instead
 * (closest analogue: mutation-heavy, multiple cards, immediate-apply
 * settings).
 *
 * Every mutation/query below is a straight duplicate of
 * OwnerProfilePageClassic.tsx's own — same query keys (["tenant-settings"]),
 * same payload shapes, same validation, same confirm-gate-before-save
 * pattern. Nothing here reimplements any of Classic's logic; this is a thin
 * re-run of the exact same calls, restyled, matching every other screen's
 * own Classic/Modern tradeoff.
 */
export function OwnerProfilePageModern() {
  const { session, setSession, logoutAllDevices } = useAuth();
  const queryClient = useQueryClient();
  const signOutAllConfirm = useConfirmGate();
  const [signOutAllPending, setSignOutAllPending] = useState(false);

  const [fullName, setFullName] = useState(session?.user.fullName ?? "");
  const [email, setEmail] = useState(session?.user.email ?? "");
  const { notice: nameNotice, showSuccess: showNameSuccess, showError: showNameError } = useNotice();
  const nameConfirm = useConfirmGate();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { notice: passwordNotice, showSuccess: showPasswordSuccess, showError: showPasswordError } = useNotice();
  const passwordConfirm = useConfirmGate();

  const nameMutation = useMutation({
    mutationFn: () => authApi.updateProfile(fullName, email),
    onSuccess: (result) => {
      if (session) setSession({ ...session, user: { ...session.user, fullName: result.fullName, email: result.email } });
      showNameSuccess("Saved.");
    },
    onError: (err) => showNameError(err instanceof ApiError ? err.message : "Could not save your profile."),
  });

  const [sessionTtlHours, setSessionTtlHours] = useState("");
  const { notice: sessionTtlNotice, showSuccess: showSessionTtlSuccess, showError: showSessionTtlError } = useNotice();
  const sessionTtlConfirm = useConfirmGate();
  const isOwner = session?.role === "owner";

  const settingsQuery = useQuery({
    queryKey: ["tenant-settings"],
    queryFn: () => tenantSettingsApi.get(),
    enabled: isOwner,
  });

  const sessionTtlMutation = useMutation({
    mutationFn: () => {
      const hours = Number(sessionTtlHours);
      if (!Number.isInteger(hours) || hours < 1) throw new Error("Enter a whole number of hours, at least 1.");
      return tenantSettingsApi.update({ sessionTtlHours: hours });
    },
    onSuccess: (result) => {
      queryClient.setQueryData(["tenant-settings"], result);
      showSessionTtlSuccess("Saved. This applies to sessions signed in from now on — it doesn't shorten anyone already signed in.");
    },
    onError: (err) => showSessionTtlError(err instanceof ApiError || err instanceof Error ? err.message : "Could not save this setting."),
  });

  const sessionTtlValue = sessionTtlHours || (settingsQuery.data ? String(settingsQuery.data.settings.sessionTtlHours) : "");

  const [trustedDeviceDays, setTrustedDeviceDays] = useState("");
  const { notice: trustedDeviceNotice, showSuccess: showTrustedDeviceSuccess, showError: showTrustedDeviceError } = useNotice();
  const trustedDeviceConfirm = useConfirmGate();

  const trustedDeviceMutation = useMutation({
    mutationFn: () => {
      const days = Number(trustedDeviceDays);
      if (!Number.isInteger(days) || days < 7 || days > 30) throw new Error("Enter a whole number of days, between 7 and 30.");
      return tenantSettingsApi.update({ trustedDeviceDays: days });
    },
    onSuccess: (result) => {
      queryClient.setQueryData(["tenant-settings"], result);
      showTrustedDeviceSuccess("Saved. This applies the next time someone checks \"remember this device.\"");
    },
    onError: (err) => showTrustedDeviceError(err instanceof ApiError || err instanceof Error ? err.message : "Could not save this setting."),
  });

  const trustedDeviceValue = trustedDeviceDays || (settingsQuery.data ? String(settingsQuery.data.settings.trustedDeviceDays) : "");

  const passwordMutation = useMutation({
    mutationFn: () => {
      if (newPassword !== confirmPassword) throw new Error("The new password and confirmation don't match.");
      if (newPassword === currentPassword) throw new Error("New password must be different from your current password.");
      return authApi.changePassword(currentPassword, newPassword);
    },
    onSuccess: () => {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      showPasswordSuccess("Password changed.");
    },
    onError: (err) => showPasswordError(err instanceof ApiError || err instanceof Error ? err.message : "Could not change your password."),
  });

  return (
    <div className="mx-auto max-w-xl">
      <div className="mb-5">
        <Eyebrow className="mb-2 text-[11px] text-[#0f6d86]">Account</Eyebrow>
        <h1 className="m-0 text-[28px] font-bold text-[#071a33]">User profile</h1>
      </div>

      <ModernCard className="mb-4">
        <h2 className="m-0 mb-3 text-[15px] font-bold text-[#071a33]">Your details</h2>
        <ModernNoticeBanner notice={nameNotice} />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            nameConfirm.request(() => nameMutation.mutate());
          }}
        >
          <ModernField label="Full name" htmlFor="m-fullName" required>
            <ModernInput id="m-fullName" required value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </ModernField>
          <ModernField label="Email" htmlFor="m-email" required>
            <ModernInput id="m-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            <span className="mt-1 block text-[12px] text-[#8b97a8]">Also your login email.</span>
          </ModernField>
          <ModernButton type="submit" disabled={nameMutation.isPending}>
            Save profile
          </ModernButton>
        </form>
      </ModernCard>

      {isOwner && (
        <ModernCard className="mb-4">
          <h2 className="m-0 mb-1 text-[15px] font-bold text-[#071a33]">Session timeout</h2>
          <p className="m-0 mb-4 text-[12.5px] text-[#6b7a8f]">
            How long a signed-in session stays valid before needing to log in again, shop-wide.
          </p>
          <ModernNoticeBanner notice={sessionTtlNotice} />
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              sessionTtlConfirm.request(() => sessionTtlMutation.mutate());
            }}
          >
            <ModernField label="Hours" htmlFor="m-sessionTtlHours" className="!mb-0">
              <ModernInput
                id="m-sessionTtlHours"
                inputMode="numeric"
                className="max-w-[8rem]"
                value={sessionTtlValue}
                onChange={(e) => setSessionTtlHours(e.target.value)}
              />
            </ModernField>
            <ModernButton type="submit" disabled={sessionTtlMutation.isPending || settingsQuery.isLoading}>
              Save
            </ModernButton>
          </form>
        </ModernCard>
      )}

      {isOwner && (
        <ModernCard className="mb-4">
          <h2 className="m-0 mb-1 text-[15px] font-bold text-[#071a33]">Remember this device</h2>
          <p className="m-0 mb-4 text-[12.5px] text-[#6b7a8f]">
            How long "remember this device" (at the two-factor sign-in step) skips the code, once someone checks it.
          </p>
          <ModernNoticeBanner notice={trustedDeviceNotice} />
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              trustedDeviceConfirm.request(() => trustedDeviceMutation.mutate());
            }}
          >
            <ModernField label="Days (7-30)" htmlFor="m-trustedDeviceDays" className="!mb-0">
              <ModernInput
                id="m-trustedDeviceDays"
                inputMode="numeric"
                className="max-w-[8rem]"
                value={trustedDeviceValue}
                onChange={(e) => setTrustedDeviceDays(e.target.value)}
              />
            </ModernField>
            <ModernButton type="submit" disabled={trustedDeviceMutation.isPending || settingsQuery.isLoading}>
              Save
            </ModernButton>
          </form>
        </ModernCard>
      )}

      {isOwner && <TwoFactorAuthCard />}

      <ModernCard>
        <h2 className="m-0 mb-3 text-[15px] font-bold text-[#071a33]">Change password</h2>
        <ModernNoticeBanner notice={passwordNotice} />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            passwordConfirm.request(() => passwordMutation.mutate());
          }}
        >
          <ModernField label="Current password" htmlFor="m-currentPassword" required>
            <ModernPasswordInput
              id="m-currentPassword"
              required
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </ModernField>
          <ModernField label="New password" htmlFor="m-newPassword" required>
            <ModernPasswordInput
              id="m-newPassword"
              required
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <ModernPasswordRequirementsList password={newPassword} />
            <ModernPasswordStrengthMeter password={newPassword} />
            {newPassword && newPassword === currentPassword && (
              <span className="mt-1.5 block text-[12px] text-[#b3261e]">Must be different from your current password.</span>
            )}
          </ModernField>
          <ModernField label="Confirm new password" htmlFor="m-confirmPassword" required>
            <ModernPasswordInput
              id="m-confirmPassword"
              required
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </ModernField>
          <ModernButton
            type="submit"
            disabled={
              passwordMutation.isPending ||
              !isPasswordComplexEnough(newPassword) ||
              newPassword !== confirmPassword ||
              newPassword === currentPassword
            }
          >
            Change password
          </ModernButton>
        </form>
      </ModernCard>

      <ModernCard className="mt-4">
        <h2 className="m-0 mb-1 text-[15px] font-bold text-[#071a33]">Sign out of all devices</h2>
        <p className="m-0 mb-4 text-[12.5px] text-[#6b7a8f]">
          Ends every signed-in session for your account everywhere — including this one. Use this if you think a device
          you're no longer using still has you signed in.
        </p>
        <ModernButton
          type="button"
          variant="danger"
          disabled={signOutAllPending}
          onClick={() =>
            signOutAllConfirm.request(() => {
              setSignOutAllPending(true);
              void logoutAllDevices();
            })
          }
        >
          Sign out everywhere
        </ModernButton>
      </ModernCard>

      <ModernConfirmDialog {...nameConfirm.dialogProps} message="Save these profile changes?" />
      <ModernConfirmDialog {...passwordConfirm.dialogProps} message="Change your password now?" confirmLabel="Yes, Change It" />
      <ModernConfirmDialog {...sessionTtlConfirm.dialogProps} message="Save this session timeout? It applies to sessions signed in from now on." />
      <ModernConfirmDialog
        {...trustedDeviceConfirm.dialogProps}
        message='Save this? It applies the next time someone checks "remember this device."'
      />
      <ModernConfirmDialog
        {...signOutAllConfirm.dialogProps}
        message="Sign out of every device, including this one? You'll need to sign in again."
        confirmLabel="Yes, Sign Out Everywhere"
      />
    </div>
  );
}
